/**
 * PublicSitePage — Iter 175h.
 *
 * Live preview / public renderer for a builder site at `/site/:subdomain`.
 *
 * Reads from the public, no-auth `GET /api/public/sites/<subdomain>`
 * endpoint and renders whatever the master has *saved* in the editor
 * (draft, not just published) — so the share link reflects every save
 * in near real-time when the client refreshes.
 *
 * Block renderers here are intentionally minimal; the live editor is
 * the source of truth for styling so we re-use FluidCanvas for the most
 * common modern section type (fluid_canvas) in read-only mode and ship
 * simple inline renderers for hero/text/products/gallery/etc.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShoppingBag, AlertTriangle, Plus } from 'lucide-react';
import FluidCanvas from './website-builder/sqs/FluidCanvas';
import { SiteHeader, SiteFooter } from './website-builder/site-chrome/SiteChrome';
// Iter 191x — Announcement banner + Promotional pop-up
import AnnouncementBanner from '@/components/storefront/AnnouncementBanner';
import PromotionalPopup from '@/components/storefront/PromotionalPopup';
import PublicCookieBanner from '@/components/PublicCookieBanner';
import SEOHead from '@/components/SEOHead';
import { useCart } from '@/context/CartContext';
import { useCartDrawer } from '@/components/storefront/CartDrawer';
// Iter 195 — Scroll-reveal + ProductReviews
import '@/components/storefront/ScrollReveal';
import ProductReviews from '@/components/storefront/ProductReviews';
// Iter 196 — Premium Defaults pass
import PremiumSection, { ASPECT_RATIOS } from '@/components/storefront/PremiumSection';
import PremiumButton from '@/components/storefront/PremiumButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PublicSitePage() {
  const { subdomain } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErr(null);
    axios.get(`${API}/public/sites/${encodeURIComponent(subdomain)}`)
      .then((res) => { if (!cancel) setData(res.data); })
      .catch((e) => {
        if (cancel) return;
        const status = e?.response?.status;
        if (status === 404) setErr('not_found');
        else if (status === 410) setErr('suspended');
        else setErr('error');
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [subdomain]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E0E23] text-white flex items-center justify-center" data-testid="public-site-loading">
        <Loader2 size={32} className="animate-spin text-[#A78BFA]" />
      </div>
    );
  }

  if (err) {
    const msg = err === 'not_found'
      ? "We couldn't find a site at this address."
      : err === 'suspended'
        ? 'This site is currently unavailable.'
        : 'Something went wrong loading this site.';
    return (
      <div className="min-h-screen bg-[#0E0E23] text-white flex flex-col items-center justify-center px-6 text-center" data-testid={`public-site-${err}`}>
        <AlertTriangle size={36} className="text-[#A78BFA] mb-4" />
        <h1 className="text-2xl font-bold font-syne mb-2">Site unavailable</h1>
        <p className="text-white/60 max-w-sm">{msg}</p>
        <a href="/" className="mt-6 px-5 py-2.5 rounded-xl text-sm font-bold uppercase tracking-[0.16em] bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white">
          EntrepreNatives Home
        </a>
      </div>
    );
  }

  const { site, page, products } = data || {};
  const blocks = (page?.blocks || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const brand = site?.business_name || site?.name || 'Untitled';
  const primary = site?.settings?.primary_color || '#8B5CF6';
  // Iter 196 — Site-wide premium defaults (alternating bands, etc.)
  const siteTheme = site?.settings?.premium_defaults || site?.theme?.premium_defaults || {};
  // Iter 185 — site-wide header/footer come from the persisted `site_header`
  // / `site_footer` (see marketplace/site_chrome_routes.py). Both are always
  // present on the site doc thanks to the GET-with-fallback endpoint the
  // client builder uses. If a legacy site has neither key, we skip rendering.
  const siteHeader = site?.site_header || null;
  const siteFooter = site?.site_footer || null;

  return (
    <div className="min-h-screen bg-white text-[#0E0E23]" data-testid="public-site">
      {/* Live-preview ribbon */}
      <div
        className="w-full text-center text-[10px] uppercase tracking-[0.22em] font-bold py-1.5 text-white"
        style={{ background: `linear-gradient(90deg, ${primary}, #3B82F6)` }}
        data-testid="public-site-ribbon"
      >
        Live Preview · {brand}
      </div>

      {/* Iter 191x — Announcement banner (always above header when enabled) */}
      <AnnouncementBanner announcement={site?.settings?.announcement} subdomain={subdomain} />

      {siteHeader && <SiteHeader header={siteHeader} />}

      {blocks.length === 0 ? (
        <div className="py-32 text-center text-[#0E0E23]/60" data-testid="public-site-empty">
          <p className="text-lg font-bold">Site is still being built</p>
          <p className="text-sm mt-2">Check back soon for the finished site.</p>
        </div>
      ) : (
        blocks.map((block, idx) => (
          <div key={block.id} className="wb-reveal" data-reveal="fade-up" style={{ transitionDelay: `${Math.min(idx, 4) * 60}ms` }}>
            <ReadOnlyBlock block={block} index={idx} products={products || []} brand={brand} primary={primary} siteTheme={siteTheme} />
          </div>
        ))
      )}

      {siteFooter ? (
        <SiteFooter footer={siteFooter} />
      ) : (
        <footer className="py-8 text-center text-xs text-[#0E0E23]/40 border-t border-black/5 mt-12">
          Built with <a href="/" className="font-bold hover:text-[#0E0E23] transition-colors">EntrepreNatives</a>
        </footer>
      )}

      {/* Iter 191h — Vendor-configurable cookie consent banner. */}
      <PublicCookieBanner
        cookies={site?.settings?.cookies}
        subdomain={subdomain}
      />

      {/* Iter 191m — SEO tag injection (meta, OG, verification, GA/GTM, JSON-LD). */}
      <SEOHead site={site} page={page} subdomain={subdomain} />

      {/* Iter 191x — Promotional pop-up (respects timer/scroll/frequency settings) */}
      <PromotionalPopup popup={site?.settings?.promotional_popup} subdomain={subdomain} />
    </div>
  );
}

// ─── Read-only block renderers ───────────────────────────────────────────
function ReadOnlyBlock({ block, index = 0, products, brand, primary, siteTheme = {} }) {
  if (block?.hidden) return null;
  const c = block.content || {};
  const shared = { block, index, siteTheme, accent: primary };

  switch (block.type) {
    case 'hero':
      return <ReadOnlyHero {...shared} c={c} primary={primary} />;
    case 'fluid_canvas':
      return (
        <section data-testid={`public-block-${block.type}-${block.id}`}>
          <FluidCanvas block={block} content={c} editable={false} onUpdateChild={() => {}} onAddChild={() => {}} onRemoveChild={() => {}} />
        </section>
      );
    case 'products':
      return <ReadOnlyProducts {...shared} c={c} products={products} />;
    case 'about':
    case 'custom_text':
    case 'signup':
    case 'testimonial':
      return <ReadOnlyText {...shared} c={c} />;
    case 'gallery':
      return <ReadOnlyGallery {...shared} c={c} />;
    case 'single_image':
      return <ReadOnlyImage {...shared} c={c} />;
    case 'banner':
      return <ReadOnlyBanner {...shared} c={c} brand={brand} />;
    case 'spacer':
      return <div style={{ height: c.height || 64 }} data-testid={`public-block-spacer-${block.id}`} />;
    case 'embed':
      return c.html ? (
        <PremiumSection {...shared}>
          <div dangerouslySetInnerHTML={{ __html: c.html }} />
        </PremiumSection>
      ) : null;
    default:
      return <ReadOnlyText {...shared} c={c} />;
  }
}

// ── Hero — supports full-bleed backgrounds + editorial-scale H1 ─────
function ReadOnlyHero({ block, index, siteTheme, accent, c, primary }) {
  const bg = c.background_image || c.image || c.bg_image;
  const layout = block.layout || {};
  const isFullscreen = layout.container_width === 'full-bleed' || layout.full_bleed_media !== false;
  const height = layout.hero_height === 'compact' ? '55vh'
             : layout.hero_height === 'tall' ? '95vh'
             : layout.hero_height === 'medium' ? '75vh'
             : '85vh';  // default cinematic

  return (
    <section
      className="wb-hero relative flex items-center justify-center text-center overflow-hidden"
      style={{
        minHeight: height,
        backgroundImage: bg ? `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.45)), url(${bg})` : `linear-gradient(135deg, ${primary}, #3B82F6)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: layout.parallax ? 'fixed' : 'scroll',
        color: '#fff',
        width: isFullscreen ? '100vw' : '100%',
        marginLeft: isFullscreen ? 'calc(50% - 50vw)' : 0,
      }}
      data-testid={`public-block-hero-${block.id}`}
    >
      <div className="relative z-10 px-6 sm:px-10 max-w-5xl mx-auto py-16">
        {c.eyebrow && (
          <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-bold opacity-90 mb-4 sm:mb-6">
            {c.eyebrow}
          </p>
        )}
        <h1
          className="font-syne font-bold tracking-[-0.03em] leading-[0.98]"
          style={{
            fontSize: 'clamp(48px, 9vw, 128px)',
          }}
        >
          {c.title || c.headline || 'Welcome'}
        </h1>
        {c.subtitle && (
          <p className="mt-5 sm:mt-7 mx-auto opacity-90 leading-relaxed"
             style={{ maxWidth: '640px', fontSize: 'clamp(15px, 1.4vw, 20px)' }}>
            {c.subtitle}
          </p>
        )}
        {c.cta_text && (
          <div className="mt-8 sm:mt-10 flex items-center justify-center gap-3 flex-wrap">
            <PremiumButton
              label={c.cta_text}
              href={c.cta_link || '#'}
              color="#FFFFFF"
              fg="#0E0E23"
              size="lg"
            />
            {c.cta_secondary_text && (
              <PremiumButton
                label={c.cta_secondary_text}
                href={c.cta_secondary_link || '#'}
                variant="outline"
                color="#FFFFFF"
                fg="#FFFFFF"
                size="lg"
                showArrow={false}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function ReadOnlyText({ block, index, siteTheme, accent, c }) {
  if (!c.title && !c.subtitle && !c.body && !c.text) return null;
  return (
    <PremiumSection block={block} index={index} siteTheme={siteTheme} accent={accent}>
      <div className="max-w-3xl mx-auto text-center">
        {c.eyebrow && <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-bold opacity-70 mb-4">{c.eyebrow}</p>}
        {c.title && (
          <h2 className="font-syne font-bold tracking-[-0.02em] leading-[1.05]"
              style={{ fontSize: 'clamp(32px, 5vw, 64px)' }}>
            {c.title}
          </h2>
        )}
        {c.subtitle && <p className="mt-4 opacity-80" style={{ fontSize: 'clamp(15px, 1.3vw, 19px)' }}>{c.subtitle}</p>}
        {(c.body || c.text) && (
          <div className="text-base sm:text-lg leading-relaxed opacity-85 mt-6 whitespace-pre-wrap">
            {c.body || c.text}
          </div>
        )}
      </div>
    </PremiumSection>
  );
}

function ReadOnlyImage({ block, index, siteTheme, accent, c }) {
  const src = c.image || c.src || c.url;
  if (!src) return null;
  const layout = block.layout || {};
  const isFullBleed = layout.full_bleed_media === true;
  const aspect = ASPECT_RATIOS[layout.aspect_ratio]?.ratio;
  const imgStyle = aspect ? { aspectRatio: aspect, objectFit: 'cover', width: '100%' } : { width: '100%' };
  const rounded = isFullBleed ? '' : 'rounded-2xl';

  if (isFullBleed) {
    return (
      <section
        className="wb-image-fullbleed relative"
        style={{ width: '100vw', marginLeft: 'calc(50% - 50vw)' }}
        data-testid={`public-block-image-${block.id}`}
      >
        <img src={src} alt={c.alt || ''} style={imgStyle} className="block" />
        {c.caption && (
          <div className="max-w-3xl mx-auto px-6 pt-4 pb-8 text-center">
            <p className="text-sm opacity-70">{c.caption}</p>
          </div>
        )}
      </section>
    );
  }

  return (
    <PremiumSection block={block} index={index} siteTheme={siteTheme} accent={accent}>
      <img src={src} alt={c.alt || ''} className={`w-full ${rounded} shadow-lg`} style={imgStyle} />
      {c.caption && <p className="text-center text-sm opacity-70 mt-5 max-w-xl mx-auto">{c.caption}</p>}
    </PremiumSection>
  );
}

function ReadOnlyGallery({ block, index, siteTheme, accent, c }) {
  const images = c.images || [];
  if (!images.length) return null;
  const layout = block.layout || {};
  const aspect = ASPECT_RATIOS[layout.aspect_ratio]?.ratio || '1 / 1';
  const cols = c.columns || 3;
  const gridCls = cols === 2 ? 'grid-cols-1 md:grid-cols-2'
              : cols === 3 ? 'grid-cols-2 md:grid-cols-3'
              : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';

  return (
    <PremiumSection block={block} index={index} siteTheme={siteTheme} accent={accent}>
      {c.title && (
        <h2 className="font-syne font-bold tracking-[-0.02em] text-center mb-12"
            style={{ fontSize: 'clamp(28px, 4.5vw, 56px)' }}>
          {c.title}
        </h2>
      )}
      <div className={`grid ${gridCls} gap-3 sm:gap-4`}>
        {images.map((img, i) => (
          <div key={i} className="overflow-hidden rounded-xl group" style={{ aspectRatio: aspect }}>
            <img
              src={typeof img === 'string' ? img : (img.src || img.url)}
              alt=""
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </PremiumSection>
  );
}

function ReadOnlyProducts({ block, index, siteTheme, accent, c, products }) {
  const ids = c.featured_product_ids || [];
  const list = ids.length
    ? ids.map(id => products.find(p => p.id === id || p.product_id === id)).filter(Boolean)
    : products.slice(0, 6);
  const { addItem } = useCart();
  const { setOpen } = useCartDrawer();
  const handleAdd = (p) => {
    addItem({
      id: p.product_id || p.id,
      name: p.name,
      price: Number(p.price || 0),
      images: p.images || (p.image_url ? [p.image_url] : []),
    }, '', 1);
    setOpen(true);
  };
  // Editorial default: 4:5 portrait cards; overridable per block
  const layout = block.layout || {};
  const aspect = ASPECT_RATIOS[layout.aspect_ratio]?.ratio || '4 / 5';
  return (
    <PremiumSection block={block} index={index} siteTheme={siteTheme} accent={accent}>
      <div className="text-center mb-14">
        {c.eyebrow && <p className="text-[10px] uppercase tracking-[0.28em] font-bold opacity-70 mb-4">{c.eyebrow}</p>}
        {c.title && (
          <h2 className="font-syne font-bold tracking-[-0.02em] leading-[1.05]"
              style={{ fontSize: 'clamp(32px, 5vw, 64px)' }}>
            {c.title}
          </h2>
        )}
        {c.subtitle && <p className="text-base sm:text-lg opacity-75 mt-4 max-w-2xl mx-auto">{c.subtitle}</p>}
      </div>
      {list.length === 0 ? (
        <div className="text-center opacity-60">
          <ShoppingBag size={36} className="mx-auto mb-3" />
          <p className="text-sm">Products coming soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {list.map((p) => {
            const pid = p.product_id || p.id;
            return (
              <div
                key={pid}
                className="storefront-product-card group relative overflow-hidden transition-all duration-300"
                data-testid={`public-product-card-${pid}`}
              >
                {/* Image — locked aspect ratio for editorial rhythm */}
                <div className="relative overflow-hidden rounded-xl" style={{ aspectRatio: aspect }}>
                  {(p.images?.[0] || p.image_url) ? (
                    <img
                      src={p.images?.[0] || p.image_url}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center opacity-40" style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #4C1D95 100%)' }}>
                      <ShoppingBag size={40} className="text-white/60" />
                    </div>
                  )}
                  {p.sale_price && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: 'linear-gradient(135deg, #EC4899, #F97316)' }}>
                      Sale
                    </span>
                  )}
                  {/* Hover add-to-cart — larger, editorial */}
                  <button
                    onClick={() => handleAdd(p)}
                    className="absolute inset-x-3 bottom-3 h-11 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] text-white inline-flex items-center justify-center gap-2 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                    style={{ background: 'rgba(14,14,35,0.92)', backdropFilter: 'blur(10px)' }}
                    data-testid={`public-add-cart-${pid}`}
                  >
                    <Plus size={12} /> Add to cart
                  </button>
                </div>

                {/* Info */}
                <div className="pt-4 pb-2 px-1">
                  <p className="font-bold text-sm sm:text-base">{p.name}</p>
                  {typeof p.price !== 'undefined' && (
                    <p className="text-sm mt-1 opacity-75">
                      {p.sale_price ? (
                        <>
                          <span className="line-through opacity-50 mr-2">${Number(p.price).toFixed(2)}</span>
                          <span style={{ color: '#EC4899' }}>${Number(p.sale_price).toFixed(2)}</span>
                        </>
                      ) : (
                        <>${Number(p.price).toFixed(2)}</>
                      )}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PremiumSection>
  );
}

function ReadOnlyBanner({ block, index, siteTheme, accent, c, brand }) {
  return (
    <PremiumSection block={block} index={index} siteTheme={siteTheme} accent={accent}>
      <div className="text-center max-w-3xl mx-auto">
        {c.eyebrow && <p className="text-[10px] uppercase tracking-[0.28em] font-bold opacity-70 mb-4">{c.eyebrow}</p>}
        <h2 className="font-syne font-bold tracking-[-0.02em] leading-[1.05]"
            style={{ fontSize: 'clamp(32px, 5.5vw, 72px)' }}>
          {c.title || c.headline || brand}
        </h2>
        {c.subtitle && <p className="mt-5 opacity-80 leading-relaxed" style={{ fontSize: 'clamp(15px, 1.3vw, 19px)' }}>{c.subtitle}</p>}
        {c.cta_text && (
          <div className="mt-8 flex items-center justify-center">
            <PremiumButton
              label={c.cta_text}
              href={c.cta_link || '#'}
              color={accent}
              fg="#FFFFFF"
              size="md"
            />
          </div>
        )}
      </div>
    </PremiumSection>
  );
}
